import { moveItem, nudgeIndex } from 'BomdisoftComponents/reorder/order';
import { createStore, type StoreApi } from '@bomdisoft/store/vanilla';

export type ReorderAxis = 'grid' | 'list';

/** How the destination is drawn.
 *
 * `slot` opens a reserved well where the item will land, which reads as the
 * arrangement you are about to get. It needs a layout that can reflow, so it
 * suits a wrapping board.
 *
 * `mark` rules the edge the item lands against and moves nothing until the drop.
 * A table column is one width shared by every row, so opening a gap would shift
 * every column to the right of it while the reader is still aiming. Picked for
 * the DataGrid 2026-08-25.
 */
export type ReorderTreatment = 'slot' | 'mark';

export type ReorderEngineOptions = {
  axis?: ReorderAxis;
  ids: readonly string[];
  treatment?: ReorderTreatment;
  onReorder: (ids: readonly string[]) => void;
  /** Announced on every move. Position is one-based. */
  announce?: (id: string, position: number, total: number) => void;
  /** Refuses a KEYBOARD or TAP move the surface does not allow. Deliberately not
   *  applied to a pointer drop: the DataGrid refuses to nudge a column across
   *  the frozen boundary, because crossing it is pinning and pinning is the pin
   *  control's job, while a DROP across it is exactly how a column gets pinned.
   *  Gating both broke every frozen-boundary test at once. */
  canMove?: (fromId: string, toId: string) => boolean;
  /** Runs before the order is emitted on a drop, for a surface with a second
   *  model to settle first. The grid pins or unpins here, because where the
   *  column lands depends on it. */
  onBeforeDrop?: (movedId: string, overId: string) => void;
};

export type ReorderMark = { id: string; side: 'before' | 'after' };

export type ReorderHeld = { id: string; origin: readonly string[] };

export type ReorderState = {
  dragging: string | null;
  held: ReorderHeld | null;
  mark: ReorderMark | null;
  slotIndex: number | null;
};

export type ReorderDragEvent = {
  readonly clientX: number;
  readonly clientY: number;
  readonly dataTransfer: Pick<DataTransfer, 'dropEffect' | 'effectAllowed' | 'setData' | 'setDragImage'> | null;
  preventDefault: () => void;
  stopPropagation: () => void;
};

export type ReorderKeyEvent = {
  readonly key: string;
  preventDefault: () => void;
};

/**
 * The item being dragged, held here rather than read back from `dataTransfer`.
 *
 * `getData` returns an empty string during `dragover`: the browser withholds the
 * payload until the drop, so a target that reads it mid-drag sees nothing, skips
 * its `preventDefault()` and never becomes a valid drop target. `setData` is
 * still called on `dragstart`, because a drag with no payload is not a drag at
 * all in Firefox.
 */
let draggedId: string | null = null;

export const createReorderEngine = (initial: ReorderEngineOptions) => {
  let options = initial;
  const store: StoreApi<ReorderState> = createStore<ReorderState>()(() => ({ dragging: null, held: null, mark: null, slotIndex: null }));
  const elements = new Map<string, HTMLElement>();
  const order = () => [...options.ids];
  const treatment = () => options.treatment ?? 'slot';

  const register = (id: string, element: HTMLElement | null) => {
    if (element) elements.set(id, element);
    else elements.delete(id);
  };

  const endDrag = () => {
    draggedId = null;
    store.setState({ dragging: null, mark: null, slotIndex: null });
  };

  const togglePick = (id: string) => {
    const current = order();
    const index = current.indexOf(id);
    if (index < 0) return;
    store.setState(state => ({ held: state.held?.id === id ? null : { id, origin: current } }));
    options.announce?.(id, index + 1, current.length);
  };

  const place = (id: string, held: ReorderHeld | null = store.getState().held) => {
    if (!held || held.id === id) return;
    const current = order();
    const from = current.indexOf(held.id);
    const to = current.indexOf(id);
    if (from < 0 || to < 0) return;
    if (options.canMove && !options.canMove(held.id, id)) return;
    options.onReorder(moveItem(current, from, to));
    store.setState({ held: null });
    options.announce?.(held.id, to + 1, current.length);
  };

  const dragStart = (id: string, event: ReorderDragEvent) => {
    draggedId = id;
    const current = order();
    const isSlot = treatment() === 'slot';
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', id);
    }
    // The grip carries the `draggable` attribute, so without this the browser
    // drags a picture of the handle rather than the thing being moved.
    const element = elements.get(id);
    if (element && event.dataTransfer) {
      const rect = element.getBoundingClientRect();
      event.dataTransfer.setDragImage(element, event.clientX - rect.left, event.clientY - rect.top);
    }
    // DEFERRED, and it is load-bearing for the `slot` treatment. The browser
    // takes its drag image and binds the session during `dragstart`; taking
    // the source out of the layout in the same tick ends the drag on the spot.
    // Measured twice: a real mouse drag on a `slot` board yields dragstart then
    // dragend and nothing else, while the same drag on a `mark` grid, which
    // only dims and never hides, runs the full sequence and drops. A tick
    // later the session is bound and hiding is safe.
    setTimeout(() => {
      if (draggedId !== id) return;
      store.setState(isSlot ? { dragging: id, slotIndex: current.indexOf(id) } : { dragging: id });
    }, 0);
  };

  const dragOver = (overId: string, event: ReorderDragEvent) => {
    if (!draggedId) return;
    // The item's own place is refused, and the refusal has to be kept from the
    // surface behind it: these events bubble to the container, which accepts
    // the drag everywhere, and without this it would overrule the refusal and
    // light the move cursor over the very item being dragged.
    if (draggedId === overId) {
      event.stopPropagation();
      return;
    }
    // Unconditional: a target that does not preventDefault here is not a drop
    // target at all, and the browser fires no drop on it.
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const current = order();
    const forward = current.indexOf(overId) > current.indexOf(draggedId);
    if (treatment() === 'mark') {
      store.setState({ mark: { id: overId, side: forward ? 'after' : 'before' } });
      return;
    }
    const rest = current.filter(id => id !== draggedId);
    const at = rest.indexOf(overId);
    if (at < 0) return;
    store.setState({ slotIndex: forward ? at + 1 : at });
  };

  const drop = (overId: string, event: ReorderDragEvent) => {
    event.preventDefault();
    const moved = draggedId;
    endDrag();
    if (!moved || moved === overId) return;
    options.onBeforeDrop?.(moved, overId);
    const current = order();
    const from = current.indexOf(moved);
    const to = current.indexOf(overId);
    if (from < 0 || to < 0) return;
    options.onReorder(moveItem(current, from, to));
    options.announce?.(moved, to + 1, current.length);
  };

  /**
   * Where the indicator says the item will land, as a whole order.
   *
   * The item handlers work from the element under the cursor, but the slot and
   * the empty surface are not items and have no `overId` to work from. What they
   * have is the destination already on screen, which is the only thing the
   * reader was ever promised. `null` when that destination is where the item
   * already is, so a drop that changes nothing emits nothing.
   */
  const indicatedOrder = (): readonly string[] | null => {
    if (!draggedId) return null;
    const current = order();
    const { mark, slotIndex } = store.getState();
    const rest = current.filter(id => id !== draggedId);
    let at: number | null = null;
    if (treatment() === 'slot') {
      at = slotIndex;
    } else if (mark) {
      const found = rest.indexOf(mark.id);
      if (found >= 0) at = mark.side === 'after' ? found + 1 : found;
    }
    if (at === null || at < 0) return null;
    const next = [...rest.slice(0, at), draggedId, ...rest.slice(at)];
    return next.every((id, index) => id === current[index]) ? null : next;
  };

  /**
   * The reserved slot and the container's empty space.
   *
   * Both were plain elements with no handlers, so neither ever called
   * `preventDefault` and the browser refused to fire a drop on either. The slot
   * is drawn precisely to say "it lands here", so releasing over it is the most
   * natural way to finish the gesture, and it was the one place in the board
   * where finishing did nothing: the item sprang back and nothing said why.
   *
   * The drop guard is `draggedId` rather than a flag of its own. An item's own
   * `onDrop` ends the drag before the event bubbles up here, so a drop that a
   * real target already handled arrives with nothing left to move.
   */
  const surfaceDragOver = (event: ReorderDragEvent) => {
    if (!draggedId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  };

  const surfaceDrop = (event: ReorderDragEvent) => {
    if (!draggedId) return;
    event.preventDefault();
    const moved = draggedId;
    const next = indicatedOrder();
    endDrag();
    if (!next) return;
    options.onReorder(next);
    options.announce?.(moved, next.indexOf(moved) + 1, next.length);
  };

  const keyDown = (id: string, event: ReorderKeyEvent) => {
    const current = order();
    const index = current.indexOf(id);
    if (index < 0) return;
    const { held } = store.getState();
    const isHeld = held?.id === id;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      togglePick(id);
      return;
    }

    if (event.key === 'Escape' && isHeld && held) {
      event.preventDefault();
      options.onReorder(held.origin);
      store.setState({ held: null });
      return;
    }

    if (!isHeld) return;

    const back = (options.axis ?? 'list') === 'list' ? 'ArrowUp' : 'ArrowLeft';
    const forward = (options.axis ?? 'list') === 'list' ? 'ArrowDown' : 'ArrowRight';
    const direction = event.key === back ? -1 : event.key === forward ? 1 : 0;
    if (direction === 0) return;
    const next = nudgeIndex(index, direction, current.length);
    if (next === null) return;
    const target = current[next];
    if (!target) return;
    if (options.canMove && !options.canMove(id, target)) return;
    event.preventDefault();
    options.onReorder(moveItem(current, index, next));
    options.announce?.(id, next + 1, current.length);
  };

  return {
    dragOver,
    dragStart,
    drop,
    endDrag,
    keyDown,
    place,
    register,
    setOptions: (next: ReorderEngineOptions) => {
      options = next;
    },
    store,
    surfaceDragOver,
    surfaceDrop,
    togglePick,
  };
};

export type ReorderEngine = ReturnType<typeof createReorderEngine>;
